import * as React from 'react'
import {
  FaIcon,
  faTriangleExclamationRegular,
  faChevronDown,
  faChevronRight,
  faFileLinesSolid,
  faShieldCheck,
  faShieldXmark,
  faShieldExclamation,
  faCircleCheck,
  faCircleExclamation,
  faCircleXmark,
  faClock,
} from '@/lib/fa'
import { getCertificateStatus } from '@/app/services/import/certificateStatusResolver'
import { getReadStatus } from '@/app/services/import/readStatusResolver'
import { CertificateStatus } from '@/app/constants/import/certificateStatus'
import { ReadStatus } from '@/app/constants/import/readStatus'
import { FileType } from '@/app/constants/fileType'
import { ProcessedStatus } from '@/app/models/import/processedStatus'
import { getProcessedStatus } from '@/app/services/import/processedStatusResolver'
import { formatName, isTachographFile } from '../utils/fileKind'
import type { UploadedFileApiDto } from '../model/imports.api.dto'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type FileId = string

type Props = {
  filesById: Record<FileId, UploadedFileApiDto>
  customLogs: Record<string, string>
  title?: string
  /** When true, section starts collapsed. Default: true */
  defaultCollapsed?: boolean
  onFileDetailsClick?: (fileId: string) => void
}

function resolveCertificateStatus(raw: any): CertificateStatus {
  if (typeof raw === 'number') return getCertificateStatus(raw)
  if (typeof raw === 'string' && Object.values(CertificateStatus).includes(raw as CertificateStatus)) return raw as CertificateStatus
  return CertificateStatus.Unknown
}

function resolveReadStatus(raw: any): ReadStatus {
  if (typeof raw === 'number') return getReadStatus(raw)
  if (typeof raw === 'string' && Object.values(ReadStatus).includes(raw as ReadStatus)) return raw as ReadStatus
  return ReadStatus.Unknown
}

function resolveProcessedStatus(raw: any): ProcessedStatus {
  if (typeof raw === 'number') return getProcessedStatus(raw)
  if (typeof raw === 'string' && Object.values(ProcessedStatus).includes(raw as ProcessedStatus)) return raw as ProcessedStatus
  return ProcessedStatus.None
}

const CertificateStatusBadge: React.FC<{ status: CertificateStatus }> = ({ status }) => {
  const { t } = useLocalize()
  switch (status) {
    case CertificateStatus.Valid:
      return (
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-content-success tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-success">
          <FaIcon icon={faShieldCheck} />
          {t('importModule.cert-valid')}
        </span>
      )
    case CertificateStatus.Invalid:
      return (
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-content-error tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-error">
          <FaIcon icon={faShieldXmark} />
          {t('importModule.cert-invalid')}
        </span>
      )
    default:
      return (
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-base-200 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-neutral">
          <FaIcon icon={faShieldExclamation} />
          {t('importModule.cert-unknown')}
        </span>
      )
  }
}

const ReadStatusBadge: React.FC<{ status: ReadStatus }> = ({ status }) => {
  const { t } = useLocalize()
  switch (status) {
    case ReadStatus.Complete:
      return (
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-content-success tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-success">
          <FaIcon icon={faCircleCheck} />
          {t('importModule.read-complete')}
        </span>
      )
    case ReadStatus.Warning:
      return (
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-content-warning tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-warning">
          <FaIcon icon={faCircleExclamation} />
          {t('common.warning')}
        </span>
      )
    case ReadStatus.Error:
    case ReadStatus.Terminated:
      return (
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-content-error tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-error">
          <FaIcon icon={faCircleXmark} />
          {status === ReadStatus.Terminated ? t('importModule.read-terminated') : t('common.error')}
        </span>
      )
    case ReadStatus.Waiting:
      return (
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-base-200 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-neutral">
          <FaIcon icon={faClock} />
          {t('importModule.read-waiting')}
        </span>
      )
    default:
      return null
  }
}

const DataStatusBadge: React.FC<{ status: ProcessedStatus }> = ({ status }) => {
  const { t } = useLocalize()
  switch (status) {
    case ProcessedStatus.All:
      return (
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-content-success tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-success">
          <FaIcon icon={faCircleCheck} />
          {t('importModule.data-status-valid')}
        </span>
      )
    case ProcessedStatus.Some:
      return (
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-content-warning tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-warning">
          <FaIcon icon={faCircleExclamation} />
          {t('importModule.data-status-with-errors')}
        </span>
      )
    default:
      return (
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-content-error tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-error">
          <FaIcon icon={faCircleXmark} />
          {t('importModule.data-status-invalid')}
        </span>
      )
  }
}

/** Parse server logs into a list of human-readable error strings. */
export function parseErrors(raw?: string): string[] {
  if (!raw) return []

  const trimmed = raw.trim()
  if (!trimmed) return []

  // Try JSON formats first
  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed)

      if (Array.isArray(parsed)) {
        return parsed
          .map((x) => (typeof x === 'string' ? x : JSON.stringify(x)))
          .map((s) => s.trim())
          .filter(Boolean)
      }

      if (parsed && typeof parsed === 'object') {
        const anyObj = parsed as any

        // Common shapes: { errors: [...] } or { messages: [...] }
        const arr = anyObj.errors ?? anyObj.messages
        if (Array.isArray(arr)) {
          return arr
            .map((x: any) => (typeof x === 'string' ? x : JSON.stringify(x)))
            .map((s: string) => s.trim())
            .filter(Boolean)
        }

        // Fallback: stringify the object
        return [JSON.stringify(parsed, null, 2)].filter(Boolean)
      }
    } catch {
      // If JSON parse fails, continue with text splitting
    }
  }

  // Text splitting fallbacks
  const parts = trimmed
    .split(/\r?\n|[;|]+/g)
    .map((s) => s.trim())
    .filter(Boolean)

  // If it's one huge line, try to split on ". " in a safe-ish way
  if (parts.length <= 1 && trimmed.length > 200) {
    const sentences = trimmed
      .split(/(?<=[.!?])\s+(?=[A-ZĄĆĘŁŃÓŚŹŻ])/g)
      .map((s) => s.trim())
      .filter(Boolean)
    return sentences.length > 1 ? sentences : parts
  }

  return parts
}

export const ImportErrorsAccordion: React.FC<Props> = ({
  filesById,
  customLogs,
  title: titleProp,
  defaultCollapsed = true,
  onFileDetailsClick,
}) => {
  const { t } = useLocalize()
  const title = titleProp ?? t('importModule.file-info')
  const [sectionExpanded, setSectionExpanded] = React.useState(!defaultCollapsed)
  const fileEntries = React.useMemo(() => {
    return Object.entries(filesById).map(([fileId, file]) => {
      const raw = customLogs[fileId]
      const errors = parseErrors(raw)
      const certStatus = resolveCertificateStatus(file.certificateStatus)
      const readSt = resolveReadStatus(file.readStatus)
      const dataStatus = resolveProcessedStatus(file.processedStatus)
      const isTachograph = isTachographFile(file)
      return {
        fileId,
        fileName: file.fileName,
        format: formatName(file.format),
        vehicle: file.vehicle,
        validLines: file.validLines,
        invalidLines: file.invalidLines,
        errors,
        certificateStatus: certStatus,
        readStatus: readSt,
        dataStatus,
        isTachograph,
        hasDetails: file.fileType === FileType.Ddd,
        hasIssues:
          errors.length > 0 ||
          (isTachograph
            ? certStatus === CertificateStatus.Invalid || readSt === ReadStatus.Error || readSt === ReadStatus.Terminated
            : dataStatus === ProcessedStatus.None),
        hasWarnings: isTachograph
          ? readSt === ReadStatus.Warning || certStatus === CertificateStatus.Unknown
          : dataStatus === ProcessedStatus.Some,
      }
    })
  }, [filesById, customLogs])

  const totalErrors = React.useMemo(
    () => fileEntries.reduce((sum, f) => sum + f.errors.length, 0),
    [fileEntries],
  )

  const hasAnyIssues = fileEntries.some((f) => f.hasIssues)
  const hasAnyWarnings = fileEntries.some((f) => f.hasWarnings)

  if (fileEntries.length === 0) return null

  // Determine container style based on worst status (for header and content)
  const containerBorder = hasAnyIssues
    ? 'tw-border-error/30'
    : hasAnyWarnings
      ? 'tw-border-warning/30'
      : 'tw-border-base-300'
  const containerBg = hasAnyIssues
    ? 'tw-bg-content-error'
    : hasAnyWarnings
      ? 'tw-bg-content-warning'
      : 'tw-bg-base-100'
  const iconColor = hasAnyIssues
    ? 'tw-text-error'
    : hasAnyWarnings
      ? 'tw-text-warning'
      : 'tw-text-neutral'
  const titleColor = hasAnyIssues
    ? 'tw-text-error'
    : hasAnyWarnings
      ? 'tw-text-warning'
      : 'tw-text-content-base'

  return (
    <div className={`tw-rounded-t-lg tw-border ${containerBorder} ${containerBg}`}>
      <button
        type="button"
        onClick={() => setSectionExpanded((e) => !e)}
        className={`tw-rounded-t-lg tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-left tw-text-xs tw-transition-colors hover:tw-opacity-90 ${containerBg}`}
      >
        <FaIcon
          icon={sectionExpanded ? faChevronDown : faChevronRight}
          className={`tw-h-3 tw-w-3 ${iconColor}`}
        />
        <FaIcon
          icon={hasAnyIssues || hasAnyWarnings ? faTriangleExclamationRegular : faFileLinesSolid}
          className={`tw-h-3 tw-w-3 ${iconColor}`}
        />
        <div className="tw-min-w-0 tw-flex-1 tw-text-left">
          <span className={`tw-font-medium ${titleColor}`}>{title}</span>
          {totalErrors > 0 && (
            <span className={`tw-ml-1.5 ${titleColor}`}>
              &mdash;{' '}
              <span className="tw-font-semibold">{totalErrors}</span>{' '}
              {totalErrors === 1 ? t('importModule.error-one') : totalErrors < 5 ? t('importModule.error-few') : t('importModule.error-many')}
            </span>
          )}
        </div>
      </button>

      {sectionExpanded && (
        <div className="tw-border-t tw-border-base-300/50 tw-px-4 tw-pb-4 tw-pt-3">
          <div className="tw-space-y-2">
            {fileEntries.map((f) => {
              const fileBorder = f.hasIssues
                ? 'tw-border-error/30'
                : f.hasWarnings
                  ? 'tw-border-warning/30'
                  : 'tw-border-base-300'

              return (
                <button
                  key={f.fileId}
                  type="button"
                  onClick={f.hasDetails && onFileDetailsClick ? () => onFileDetailsClick(f.fileId) : undefined}
                  className={`tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-overflow-hidden tw-rounded-md tw-border ${fileBorder} tw-bg-white tw-px-4 tw-py-3 tw-text-left ${f.hasDetails && onFileDetailsClick ? 'tw-cursor-pointer hover:tw-bg-primary/5' : 'tw-cursor-default'}`}
                >
                  <div className="tw-min-w-0 tw-flex-1">
                    <p className="tw-truncate tw-text-sm tw-font-medium tw-text-content-base">
                      {f.fileName}
                    </p>
                    <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                      {f.isTachograph ? (
                        <>
                          <CertificateStatusBadge status={f.certificateStatus} />
                          <ReadStatusBadge status={f.readStatus} />
                        </>
                      ) : (
                        <DataStatusBadge status={f.dataStatus} />
                      )}
                    </div>
                    {(f.format || f.vehicle || f.validLines != null || f.invalidLines != null) && (
                      <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-0.5 tw-text-xs tw-text-neutral">
                        {f.format && f.format !== '-' && (
                          <span><span className="tw-font-medium">{t('importModule.col-format')}:</span> {f.format}</span>
                        )}
                        {f.vehicle && f.vehicle !== '-' && (
                          <span><span className="tw-font-medium">{t('importModule.col-vehicle')}:</span> {f.vehicle}</span>
                        )}
                        {!f.isTachograph && f.validLines != null && (
                          <span><span className="tw-font-medium">{t('importModule.col-valid-lines')}:</span> {f.validLines}</span>
                        )}
                        {!f.isTachograph && f.invalidLines != null && (
                          <span><span className="tw-font-medium">{t('importModule.col-invalid-lines')}:</span> {f.invalidLines}</span>
                        )}
                      </div>
                    )}
                    {f.errors.length > 0 && (
                      <ul className="tw-mt-1.5 tw-space-y-0.5 tw-text-xs tw-text-error">
                        {f.errors.map((msg, i) => (
                          <li key={i}>{msg}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
